import { useEffect, useState } from 'react';
import { AdminGate } from '@/components/Admin/AdminGate';
import { AdminScenarioEditor } from '@/components/Admin/AdminScenarioEditor';
import { AdminScenarioList } from '@/components/Admin/AdminScenarioList';
import { adminClient, type AdminSession } from '@/persistence/adminClient';

/**
 * The admin console. One URL — /admin — for its whole lifetime.
 *
 * Unlocking, listing and editing are **phases of this route, never
 * navigations**. That is deliberate: an access code an admin opens then never
 * lands in browser history, a bookmark, a screen share, or a Referer header.
 *
 * The session token lives in this component's state and nowhere else. Not
 * localStorage or sessionStorage — readable by any script on the origin, and it
 * would outlive the reason it existed — and not the URL. The cost is that a
 * refresh means re-typing the code, which for a tool used a few times a week is
 * the right trade.
 */
export function AdminConsole() {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  /* Lock on expiry rather than waiting for the next 401. An admin who walked
     away should come back to the gate, not to a live list of every prospect. */
  useEffect(() => {
    if (!session) return;
    const ms = Date.parse(session.expiresAt) - Date.now();
    if (!Number.isFinite(ms)) return;
    const timer = window.setTimeout(() => {
      setOpen(null);
      setSession(null);
    }, Math.max(0, ms));
    return () => window.clearTimeout(timer);
  }, [session]);

  /* Same reasoning as historyClient being null: local-only has no scenario
     registry at all, so an empty list would read as "no scenarios exist". */
  if (!adminClient) {
    return (
      <main className="canvas canvas--loading">
        <div className="gate-notice" role="status">
          <h1>The admin console needs the scenario service</h1>
          <p>This build runs local-only, so there is no set of scenarios to administer.</p>
        </div>
      </main>
    );
  }

  if (!session) return <AdminGate client={adminClient} onUnlocked={setSession} />;

  const lock = () => {
    setOpen(null);
    setSession(null);
  };

  /* `key` is half of the store-isolation story — it forces a remount so no
      component-local state (autosave's pending ref, the history panel's cached
      groups) survives a scenario switch. resetEditSession, called inside
      useScenarioSession, is the other half. Neither alone is enough. */
  if (open !== null) {
    return <AdminScenarioEditor key={open} code={open} onBack={() => setOpen(null)} />;
  }

  return (
    <AdminScenarioList
      client={adminClient}
      session={session}
      onOpen={setOpen}
      onExpired={lock}
    />
  );
}
